// Plan 24c Phase B, Task 10 — Mystery Box Choice overlay (Screen 33)
//
// Shown when the local player must pick one of three minigames to play
// instead of the next round's scheduled game (triggered by a Mystery Box item).
//
// ESC is a NO-OP — the player MUST make a choice. The overlay auto-disposes
// when the server confirms the choice via mystery_box_choice_resolved.
//
// Design tokens (docs/design/tokens.json):
//   color.brand.amber          = "#E8A020"
//   color.text.secondary       = "#B0B8C8"
//   color.brand.purple-mid     = "#8040C0"
//   color.ui.overlay-dim       = "rgba(0, 0, 0, 0.60)"
//   typography.size.h2         = "36px"
//   typography.size.h4         = "22px"
//   typography.size.caption    = "14px"
//   typography.size.body       = "16px"
//   typography.shadow.title    = "0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)"
//   z-index.overlay            = 350
//   spacing.lg                 = "24px"

import { createButton } from '@client/ui/components/button';
import type { UIComponentHandle } from '@client/ui/components/types';
import type { BoardMountPayload } from './board-payload';
import type { ScreenHandle, ScreenMountContext } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type MysteryBoxMinigameInfo = {
  id: string;
  name: string;
  iconUrl?: string;
  description: string;
};

export type MysteryBoxChoicePayload = BoardMountPayload & {
  options: [MysteryBoxMinigameInfo, MysteryBoxMinigameInfo, MysteryBoxMinigameInfo];
  onChoose?: (chosenMinigameId: string) => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** color.ui.overlay-dim = "rgba(0, 0, 0, 0.60)" */
const OVERLAY_BG = 'rgba(0,0,0,0.60)';

/** color.ui.panel-bg-alpha = "rgba(30, 35, 50, 0.82)" */
const CARD_BG = 'rgba(30,35,50,0.82)';

/** color.brand.amber = "#E8A020" */
const AMBER = '#E8A020';

/** color.text.secondary = "#B0B8C8" */
const SECONDARY_TEXT = '#B0B8C8';

/** color.brand.purple-mid = "#8040C0" */
const PURPLE_MID = '#8040C0';

/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.family.body */
const BODY_FAMILY = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif";

/** typography.size.h2 = "36px" */
const H2_SIZE = '36px';

/** typography.size.h4 = "22px" */
const H4_SIZE = '22px';

/** typography.size.caption = "14px" */
const CAPTION_SIZE = '14px';

/** typography.size.body = "16px" */
const BODY_SIZE = '16px';

/** typography.shadow.title = "0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)" */
const TITLE_SHADOW = '0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createMysteryBoxChoiceScreen(
  ctx: ScreenMountContext<MysteryBoxChoicePayload>,
): ScreenHandle {
  const { host, gsapRegistry, payload } = ctx;
  const { options, onChoose } = payload;

  let disposed = false;
  const cardHandles: UIComponentHandle[] = [];

  // ── Root overlay ─────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-mystery-box-choice';
  root.setAttribute('data-screen-id', 'mystery-box-choice');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: 350`,
    `background: ${OVERLAY_BG}`,
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'justify-content: center',
    'gap: 24px',
  ].join('; ');

  // ── Title ─────────────────────────────────────────────────────────────────

  const title = document.createElement('h2');
  title.className = 'mystery-box-title';
  title.textContent = 'MYSTERY BOX — CHOOSE YOUR MINIGAME';
  title.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H2_SIZE}`,
    'font-weight: 700',
    `color: ${AMBER}`,
    `text-shadow: ${TITLE_SHADOW}`,
    'text-align: center',
    'margin: 0',
    'user-select: none',
  ].join('; ');

  root.appendChild(title);

  // ── Subtitle ──────────────────────────────────────────────────────────────

  const subtitle = document.createElement('p');
  subtitle.className = 'mystery-box-subtitle';
  subtitle.textContent =
    "Pick one of three minigames to play instead of the next round's scheduled game.";
  subtitle.style.cssText = [
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    `color: ${SECONDARY_TEXT}`,
    'text-align: center',
    'margin: 0',
    'user-select: none',
  ].join('; ');

  root.appendChild(subtitle);

  // ── Cards row ────────────────────────────────────────────────────────────

  const cardsRow = document.createElement('div');
  cardsRow.className = 'mystery-box-cards';
  cardsRow.style.cssText = [
    'display: flex',
    'flex-direction: row',
    'gap: 24px',
    'align-items: center',
    'justify-content: center',
  ].join('; ');

  // ── Build each card ───────────────────────────────────────────────────────

  options.forEach((option) => {
    // ── Card inner content ──────────────────────────────────────────────────
    const cardContent = document.createElement('div');
    cardContent.className = 'mystery-box-card-content';
    cardContent.style.cssText = [
      'width: 240px',
      'padding: 24px 28px',
      `background: ${CARD_BG}`,
      'border-radius: 16px',
      'border: 2px solid rgba(255,255,255,0.10)',
      'box-shadow: 0 4px 16px rgba(0,0,0,0.40)',
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'gap: 12px',
      'pointer-events: none',
      'user-select: none',
      'box-sizing: border-box',
    ].join('; ');

    // Icon or placeholder
    if (option.iconUrl) {
      const icon = document.createElement('img');
      icon.src = option.iconUrl;
      icon.alt = option.name;
      icon.style.cssText = 'width: 48px; height: 48px; object-fit: contain;';
      cardContent.appendChild(icon);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'mystery-box-icon-placeholder';
      placeholder.style.cssText = [
        'width: 48px',
        'height: 48px',
        `background: ${PURPLE_MID}`,
        'border-radius: 8px',
        'flex-shrink: 0',
      ].join('; ');
      cardContent.appendChild(placeholder);
    }

    // Minigame name
    const nameEl = document.createElement('h4');
    nameEl.className = 'mystery-box-card-name';
    nameEl.textContent = option.name;
    nameEl.style.cssText = [
      `font-family: ${HEADLINE_FAMILY}`,
      `font-size: ${H4_SIZE}`,
      'font-weight: 700',
      'color: #FFFFFF',
      'text-align: center',
      'margin: 0',
    ].join('; ');
    cardContent.appendChild(nameEl);

    // Description
    const descEl = document.createElement('p');
    descEl.className = 'mystery-box-card-desc';
    descEl.textContent = option.description;
    descEl.style.cssText = [
      `font-family: ${BODY_FAMILY}`,
      `font-size: ${BODY_SIZE}`,
      `color: ${SECONDARY_TEXT}`,
      'text-align: center',
      'margin: 0',
    ].join('; ');
    cardContent.appendChild(descEl);

    // Wrap in ghost button
    const capturedId = option.id;
    const cardHandle = createButton({
      gsapRegistry,
      variant: 'ghost',
      label: '',
      onClick: () => handleChoice(capturedId),
    });
    cardHandles.push(cardHandle);

    // Append the visual card content to the button element
    cardHandle.element.appendChild(cardContent);

    // Override button styles to let the card visuals take over sizing
    cardHandle.element.style.cssText += [
      '; background: transparent',
      'border: none',
      'padding: 0',
      'cursor: pointer',
    ].join('; ');

    cardsRow.appendChild(cardHandle.element);
  });

  root.appendChild(cardsRow);

  // ── Subscribe to server confirmation ──────────────────────────────────────

  const unsubscribe = payload.subscribe((msg: unknown) => {
    if (disposed) return;
    const m = msg as { type?: string; event?: { type?: string; casterId?: string } };
    if (
      m.type === 'event' &&
      m.event?.type === 'mystery_box_choice_resolved' &&
      m.event?.casterId === payload.localPlayerId
    ) {
      dispose();
    }
  });

  // ── handleChoice ──────────────────────────────────────────────────────────

  function handleChoice(chosenId: string): void {
    if (disposed) return;
    if (onChoose !== undefined && onChoose !== null) {
      onChoose(chosenId);
    } else {
      payload.send({ type: 'mystery_box_choice', chosenMinigameId: chosenId });
    }
    dispose();
  }

  // ── ESC key handler — NO-OP ───────────────────────────────────────────────
  // The player must make a choice. ESC is intentionally a no-op.

  function onEscKeydown(e: KeyboardEvent): void {
    if (disposed) return;
    if (e.key === 'Escape') {
      // Intentional no-op
    }
  }

  document.addEventListener('keydown', onEscKeydown);

  // ── Mount to host ────────────────────────────────────────────────────────

  host.appendChild(root);

  // ── dispose ──────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    unsubscribe();

    for (const handle of cardHandles) {
      handle.dispose();
    }

    document.removeEventListener('keydown', onEscKeydown);

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
